/**
 * FftWaveProvider — the one-patch FFT sea behind the WaveProvider interface (shader gaps R5, 2026-09-30).
 *
 * GPU: an OceanPatch (TSL compute, WebGPU only) — construct with a renderer whose backend is WebGPU; without one
 * (`renderer: null`) the provider is CPU-only (the harness's mode) and `tsl()` must not be called.
 *
 * CPU — THE HEIGHT QUERY (the brief's "choose, measure, state it"): not a readback (its 1–3 frame latency would put a
 * rider ~cm off a surface moving at ~1 m/s) and not a handful of top modes with their phases dropped (the old
 * `topModesToGerstner` mirror had the right statistics in the wrong places). It is the SAME band the mesh displaces,
 * summed from the SAME float32 h0 array the GPU reads, at the SAME time: every vertex-band mode, pruned only where the
 * discarded modes provably add less than `queryTailM` to the height (an L1 bound). Latency 0.
 *   The DRAWN surface is then reproduced exactly: with a drawn grid (`setDrawnGrid`) the query finds the displaced
 *   triangle of the owner's mesh over the point (three's PlaneGeometry split) and interpolates its three vertices — the
 *   rasteriser's own surface. THE TEXEL-ALIGNED GRID: when the grid spacing and origin are multiples of the patch texel
 *   (P/N), every vertex samples a texel CENTRE, so the GPU's bilinear fetch returns exactly the IFFT value there and the
 *   only differences left are the pruned tail, half-float storage and float32 phases — measured on WebGPU by the bed.
 *
 * THE SEA STATE. `setSpectrum` (the wind's speed and heading, quantised to 0.5 m/s / 5°) cross-fades the spectrum over
 * `crossFadeS` (default 20 s) — the waves grow and fade in place (ocean_patch_math: THE CROSS-FADE); the sea state's
 * amplitude scale (`mod.ampScale`, slewed by the owner) multiplies everything each frame. The heading rotation
 * `mod.dirRot` is NOT applied as a rotation (rotating a patch about the render origin slides its far side at km/s) —
 * the owner feeds the heading into the spectrum instead.
 */

import * as THREE from 'three/webgpu';
import { float, normalize, smoothstep, texture, uniform, vec2, vec3 } from 'three/tsl';
import { IDENTITY_MODULATION, type WaveModulation } from '../GerstnerWaves.js';
import type { TslGerstner, TslWaveModulation } from '../tsl-gerstner.js';
import type { TslWaveField, WaveProvider, WaveScaleAt } from '../WaveProvider.js';
import { FOOTPRINT_FADE_HI, FOOTPRINT_FADE_LO } from '../waterLook.js';
import { OceanPatch, type ComputeTarget } from './OceanPatch.js';
import { solveDrawnTriangle, type DrawnGrid } from '../drawnSurface.js';
import {
  buildH0,
  buildPatchGrid,
  jacobianGrid,
  latticeMeshMean,
  meanLiftAt,
  modesAt,
  patchCoord,
  patchStats,
  periodicBilinear,
  pruneVertexModes,
  sampleModes,
  vertexBandFits,
  vertexLattice,
  type FieldSample,
  type ModeSet,
  type OceanPatchConfig,
  type OceanSpectrumParams,
  type PatchGrid,
  type PatchStats,
} from './ocean_patch_math.js';

type FloatNode = ReturnType<typeof float>;

export interface FftWaveProviderConfig extends OceanPatchConfig {
  /** The spectrum the sea starts with. */
  spectrum: OceanSpectrumParams;
  /** Seconds a sea-state change takes to cross-fade. Default 20. */
  crossFadeS?: number;
  /** The pruning budget of the CPU query: discarded modes add at most this to a height (m). Default 0.002. */
  queryTailM?: number;
  /** The Jacobian grid the CPU foam source reads (texels per side). Default 64. */
  jacobianGridSize?: number;
}

/** The owner's drawn mesh (moved to ../drawnSurface.ts, re-exported here for the R5 callers). */
export type { DrawnGrid } from '../drawnSurface.js';

/** Quantise a spectrum request (so the wind's slow drift does not start a fade every frame). */
export function spectrumKey(p: OceanSpectrumParams): string {
  return `${Math.round(p.windSpeed * 2) / 2}|${((Math.round(p.windDirectionDeg / 5) * 5) % 360 + 360) % 360}|${p.fetchKm ?? 60}|${p.model ?? 'jonswap'}`;
}
function quantised(p: OceanSpectrumParams): OceanSpectrumParams {
  return { ...p, windSpeed: Math.max(0.5, Math.round(p.windSpeed * 2) / 2), windDirectionDeg: Math.round(p.windDirectionDeg / 5) * 5 };
}

export class FftWaveProvider implements WaveProvider {
  readonly isWaveProvider = true as const;
  readonly kind = 'fft' as const;
  readonly waves = null;
  readonly queryLatencyS = 0;
  readonly grid: PatchGrid;
  readonly patch: OceanPatch | null;
  /** (−shift) mod P per axis — render XZ + this ≡ absolute XZ (mod P): the patch is anchored to the world, not the origin. */
  readonly uPatchShift = uniform(new THREE.Vector2(0, 0));
  /** The amplitude scale when no sea-state node is given, and last frame's (the previous surface's). */
  readonly uAmp = uniform(1);
  readonly uAmpPrev = uniform(1);
  /** THE MEAN LEVEL: the Eulerian lift (m) this frame and last frame. */
  readonly uLift = uniform(0);
  readonly uLiftPrev = uniform(0);
  /** Band slope variances (amplitude scale 1) — folded into α² by the distance filter. */
  readonly uVarLow = uniform(0);
  readonly uVarShort = uniform(0);
  readonly stats = { fades: 0, fadesQueued: 0, updates: 0, cpuModeEvals: 0, cpuVertexEvals: 0, cpuGridBuilds: 0, lastKey: '' };

  private readonly crossFadeS: number;
  private readonly tailM: number;
  private readonly jM: number;
  private h0A: Float32Array;
  private h0B: Float32Array;
  private readonly h0: Float32Array; // the blend the GPU draws (normal-band texels kept current)
  private blend = 0;
  private fading = false;
  private currentKey: string;
  private pending: OceanSpectrumParams | null = null;
  private order: Int32Array;
  private tail: number;
  private modes: ModeSet | null = null;
  private modesT = NaN;
  private modesGen = -1;
  private gen = 0; // bumped whenever h0 changes
  private bandStats: PatchStats;
  private lastTime = NaN;
  private ampNow = 1;
  private liftNow = 0;
  /** The Eulerian lift at amplitude scale 1 for the last update's time (× s² = the frame's lift). */
  private liftBase = 0;
  private liftGen = -1;
  private drawn: DrawnGrid | null = null;
  /** The vertex lattice size when the drawn grid sits on one (0 = per-vertex mode sums). */
  private latticeM = 0;
  private lattice: ReturnType<typeof vertexLattice> | null = null;
  private latticeT = NaN;
  private latticeGen = -1;
  private liftQueryT = NaN;
  private liftQueryGen = -1;
  private liftQueryBase = 0;
  private readonly vertexCache = new Map<number, [number, number, number]>();
  private vertexCacheT = NaN;
  private vertexCacheGen = -1;
  private jGrid: ReturnType<typeof jacobianGrid> | null = null;
  private jGridT = NaN;
  private jGridGen = -1;
  private readonly scratch: FieldSample = { dx: 0, dy: 0, dz: 0, sx: 0, sz: 0, jxx: 0, jzz: 0, jxz: 0 };
  private readonly latticeOut: [number, number, number] = [0, 0, 0];

  constructor(renderer: ComputeTarget | null, config: FftWaveProviderConfig) {
    this.grid = buildPatchGrid(config);
    this.crossFadeS = Math.max(0.5, config.crossFadeS ?? 20);
    this.tailM = Math.max(0, config.queryTailM ?? 0.002);
    this.jM = config.jacobianGridSize ?? 64;
    const q = quantised(config.spectrum);
    this.currentKey = spectrumKey(q);
    this.stats.lastKey = this.currentKey;
    this.h0A = buildH0(this.grid, q);
    this.h0B = this.h0A.slice();
    this.h0 = this.h0A.slice();
    const pr = pruneVertexModes(this.grid, this.h0, this.tailM);
    this.order = pr.order;
    this.tail = pr.tail;
    this.bandStats = patchStats(this.grid, this.h0);
    this.uVarLow.value = this.bandStats.slopeVarLow;
    this.uVarShort.value = this.bandStats.slopeVarShort;
    this.patch = renderer ? new OceanPatch(renderer, this.grid, this.h0A, this.h0B) : null;
  }

  // ── The sea state ────────────────────────────────────────────────────────────────────────────────────────────────

  /** Request a spectrum; a new one cross-fades in (a request during a fade waits for it — the latest wins). */
  setSpectrum(p: OceanSpectrumParams): void {
    const q = quantised(p);
    const key = spectrumKey(q);
    if (this.fading) {
      if (key !== this.currentKey) { this.pending = q; this.stats.fadesQueued++; } else this.pending = null;
      return;
    }
    if (key === this.currentKey) return;
    this.startFade(q, key);
  }

  private startFade(q: OceanSpectrumParams, key: string): void {
    this.h0B = buildH0(this.grid, q);
    this.patch?.setSpectrumB(this.h0B);
    this.currentKey = key;
    this.stats.lastKey = key;
    this.blend = 0;
    this.fading = true;
    this.stats.fades++;
    this.refreshPruning();
  }

  /** Where the fade stands: the running key (the target once a fade starts), the weight, whether one waits. */
  get fade(): { key: string; blend: number; fading: boolean; pending: boolean } {
    return { key: this.currentKey, blend: this.blend, fading: this.fading, pending: this.pending !== null };
  }

  // ── Per frame ────────────────────────────────────────────────────────────────────────────────────────────────────

  setRenderShift(x: number, z: number): void {
    const P = this.grid.P;
    (this.uPatchShift.value as THREE.Vector2).set(patchCoord(-x, P), patchCoord(-z, P));
  }

  update(time: number, dt: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): void {
    this.stats.updates++;
    if (this.fading) {
      this.blend = Math.min(1, this.blend + Math.max(0, dt) / this.crossFadeS);
      if (this.blend >= 1) {
        // The fade is done: B becomes the running spectrum (the GPU reads A at w = 0 — the same field, no pop).
        this.h0A = this.h0B;
        this.patch?.setSpectrumA(this.h0A);
        this.blend = 0;
        this.fading = false;
        this.refreshPruning();
        if (this.pending) { const p = this.pending; this.pending = null; this.startFade(p, spectrumKey(p)); }
      }
      this.reblend();
    }
    const s = mod.ampScale;
    const moved = time !== this.lastTime;
    const first = this.stats.updates === 1;
    if (moved || this.gen !== this.liftGen || first) { this.liftBase = this.liftBaseAt(time); this.liftGen = this.gen; }
    const lift = s * s * this.liftBase;
    // The previous surface's scale and lift are LAST frame's (the first frame: this frame's — no bogus velocity).
    this.uAmpPrev.value = first ? s : this.ampNow;
    this.uLiftPrev.value = first ? lift : this.liftNow;
    this.ampNow = s;
    this.liftNow = lift;
    this.uAmp.value = s;
    this.uLift.value = lift;
    this.lastTime = time;
    this.patch?.dispatch(time, this.blend, moved);
  }

  /** Recompute the blended h0 (normal-band texels only) and everything derived from it. */
  private reblend(): void {
    const a = this.h0A, b = this.h0B, w = this.blend, u = 1 - w, h = this.h0;
    for (const i of this.grid.normalAll) {
      const o = i * 4;
      h[o] = a[o] * u + b[o] * w; h[o + 1] = a[o + 1] * u + b[o + 1] * w;
      h[o + 2] = a[o + 2] * u + b[o + 2] * w; h[o + 3] = a[o + 3] * u + b[o + 3] * w;
    }
    this.gen++;
    this.bandStats = patchStats(this.grid, h);
    this.uVarLow.value = this.bandStats.slopeVarLow;
    this.uVarShort.value = this.bandStats.slopeVarShort;
  }

  /** Pruning keeps every texel that matters in EITHER spectrum (so a fade never needs a re-sort mid-way). */
  private refreshPruning(): void {
    let src = this.h0A;
    if (this.fading) {
      src = new Float32Array(this.h0A.length);
      for (let i = 0; i < src.length; i++) src[i] = Math.abs(this.h0A[i]) + Math.abs(this.h0B[i]);
    }
    const pr = pruneVertexModes(this.grid, src, this.tailM);
    this.order = pr.order;
    this.tail = pr.tail; // |blend| ≤ |A| + |B| per component, so the bound holds at every w
    this.gen++;
  }

  // ── CPU queries ──────────────────────────────────────────────────────────────────────────────────────────────────

  private modesFor(t: number): ModeSet {
    if (!this.modes || t !== this.modesT || this.modesGen !== this.gen) {
      this.modes = modesAt(this.grid, this.h0, this.order, t, this.tail, this.modes ?? undefined);
      this.modesT = t;
      this.modesGen = this.gen;
    }
    return this.modes;
  }

  /** The raw vertex-band field at an ABSOLUTE point (amplitude scale 1, no lift). */
  private field(x: number, z: number, t: number): FieldSample {
    const m = this.modesFor(t);
    this.stats.cpuModeEvals++;
    return sampleModes(m, this.grid.chop, patchCoord(x, this.grid.P), patchCoord(z, this.grid.P), this.scratch);
  }

  /** The lift for a query at t: the frame's when t is the frame's time, else computed (queries off the frame clock). */
  /**
   * The lift at amplitude 1 (× s² per frame): the DRAWN mesh's own Eulerian drop when the owner's grid sits on the vertex
   * lattice (the surface the player sees — ocean_patch_math.latticeMeshMean), else the continuous surface's.
   */
  private liftBaseAt(t: number): number {
    if (this.latticeM && this.drawn) return -latticeMeshMean(this.latticeAt(t), this.drawn.spacing, 1);
    return meanLiftAt(this.grid, this.h0, t);
  }

  private latticeAt(t: number): ReturnType<typeof vertexLattice> {
    if (!this.lattice || t !== this.latticeT || this.latticeGen !== this.gen) {
      this.lattice = vertexLattice(this.grid, this.h0, t, this.latticeM);
      this.latticeT = t;
      this.latticeGen = this.gen;
      this.stats.cpuGridBuilds++;
    }
    return this.lattice;
  }

  private liftAt(t: number, s: number): number {
    if (t === this.lastTime && this.liftGen === this.gen) return s * s * this.liftBase;
    if (t !== this.liftQueryT || this.liftQueryGen !== this.gen) {
      this.liftQueryBase = this.liftBaseAt(t);
      this.liftQueryT = t;
      this.liftQueryGen = this.gen;
    }
    return s * s * this.liftQueryBase;
  }

  /** The continuous surface point that ENDS at (x, z) after the horizontal chop (fixed-point inversion, 3 steps). */
  private invert(x: number, z: number, t: number, s: number): FieldSample {
    let px = x, pz = z;
    for (let i = 0; i < 3; i++) { const f = this.field(px, pz, t); px = x - s * f.dx; pz = z - s * f.dz; }
    return this.field(px, pz, t);
  }

  sampleDisplacement(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): { dx: number; dy: number; dz: number } {
    const s = mod.ampScale;
    const f = this.field(x, z, t);
    return { dx: s * f.dx, dy: s * f.dy + this.liftAt(t, s), dz: s * f.dz };
  }

  /** Gameplay stands on the DRAWN surface (the FFT carries nothing the mesh does not draw). */
  sampleLiveHeight(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION, scaleAt?: WaveScaleAt): number {
    return this.sampleDrawnHeight(x, z, t, mod, scaleAt);
  }

  /** Set (or clear) the owner's drawn mesh so the drawn query interpolates its triangles. */
  setDrawnGrid(g: DrawnGrid | null): void {
    const same = g && this.drawn && g.originX === this.drawn.originX && g.originZ === this.drawn.originZ && g.spacing === this.drawn.spacing && g.cells === this.drawn.cells;
    if (same) return;
    this.drawn = g ? { ...g } : null;
    this.vertexCache.clear();
    this.lattice = null;
    this.liftGen = -1;
    this.liftQueryGen = -1;
    // THE VERTEX LATTICE: a grid whose spacing divides the patch into a power of two (and whose origin sits on it)
    // reads every vertex from one small CPU IFFT per frame — every vertex-band mode, no pruning.
    this.latticeM = 0;
    if (g) {
      const M = Math.round(this.grid.P / g.spacing);
      const onGrid = (v: number): boolean => Math.abs(v / g.spacing - Math.round(v / g.spacing)) < 1e-9;
      if (Math.abs(M * g.spacing - this.grid.P) < 1e-9 && vertexBandFits(this.grid, M) && onGrid(g.originX) && onGrid(g.originZ)) this.latticeM = M;
    }
  }

  /** How the drawn query reads its vertices: 'lattice' (M²) or 'modes' (pruned sums per vertex). */
  get drawnVertexSource(): string { return this.latticeM ? `lattice ${this.latticeM}²` : 'modes'; }

  /** True when the drawn grid samples texel centres (the exact case — see THE TEXEL-ALIGNED GRID). */
  get drawnGridAligned(): boolean {
    const g = this.drawn;
    if (!g) return false;
    const tx = this.grid.texel;
    const on = (v: number): boolean => Math.abs(v / tx - Math.round(v / tx)) < 1e-6;
    return on(g.spacing) && on(g.originX) && on(g.originZ);
  }

  private vertexField(i: number, j: number, t: number): [number, number, number] {
    if (t !== this.vertexCacheT || this.vertexCacheGen !== this.gen) {
      this.vertexCache.clear();
      this.vertexCacheT = t;
      this.vertexCacheGen = this.gen;
    }
    const g = this.drawn!;
    if (this.latticeM) {
      const M = this.latticeM, L = this.latticeAt(t);
      const X = Math.round(patchCoord(g.originX + i * g.spacing, this.grid.P) / g.spacing) % M;
      const Z = Math.round(patchCoord(g.originZ + j * g.spacing, this.grid.P) / g.spacing) % M;
      const k = Z * M + X;
      this.latticeOut[0] = L.dx[k]; this.latticeOut[1] = L.dy[k]; this.latticeOut[2] = L.dz[k];
      return this.latticeOut;
    }
    const key = i * 4096 + j;
    let v = this.vertexCache.get(key);
    if (!v) {
      const f = this.field(g.originX + i * g.spacing, g.originZ + j * g.spacing, t);
      v = [f.dx, f.dy, f.dz];
      this.vertexCache.set(key, v);
      this.stats.cpuVertexEvals++;
    }
    return v;
  }

  sampleDrawnHeight(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION, scaleAt?: WaveScaleAt): number {
    const s = mod.ampScale;
    const lift = this.liftAt(t, s);
    const g = this.drawn;
    if (!g) {
      const f = this.invert(x, z, t, s);
      return (s * f.dy + lift) * (scaleAt ? scaleAt(x, z) : 1);
    }
    // The displaced triangle over (x, z) — THE RIDER LAW's shared solve (../drawnSurface.ts), each vertex shoaled and
    // faded by the owner's scale exactly as the vertex stage does.
    const sp = g.spacing;
    return solveDrawnTriangle(x, z, g, (ii, jj, out) => {
      const f = this.vertexField(ii, jj, t);
      const bx = g.originX + ii * sp, bz = g.originZ + jj * sp;
      const k = scaleAt ? scaleAt(bx, bz) : 1;
      out[0] = bx + k * s * f[0];
      out[1] = k * (s * f[1] + lift);
      out[2] = bz + k * s * f[2];
    });
  }

  /** The vertex band's normal at the point (the exact normal of the choppy map, like the shader's). */
  sampleNormal(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): { nx: number; ny: number; nz: number } {
    const s = mod.ampScale;
    const f = this.field(x, z, t);
    const hx = s * f.sx, hz = s * f.sz, jxx = s * f.jxx, jzz = s * f.jzz, jxz = s * f.jxz;
    const nx = -hx * (1 + jzz) + jxz * hz;
    const ny = Math.max(0.05, (1 + jxx) * (1 + jzz) - jxz * jxz);
    const nz = -hz * (1 + jxx) + hx * jxz;
    const l = Math.hypot(nx, ny, nz) || 1;
    return { nx: nx / l, ny: ny / l, nz: nz / l };
  }

  /** The foam source: the whole band's Jacobian on a coarse CPU grid (one small IFFT per time — ocean_patch_math.jacobianGrid). */
  sampleJacobian(x: number, z: number, t: number, mod: Readonly<WaveModulation> = IDENTITY_MODULATION): number {
    if (!this.jGrid || t !== this.jGridT || this.jGridGen !== this.gen) {
      this.jGrid = jacobianGrid(this.grid, this.h0, t, this.jM);
      this.jGridT = t;
      this.jGridGen = this.gen;
      this.stats.cpuGridBuilds++;
    }
    const g = this.jGrid, P = this.grid.P, s = mod.ampScale;
    const px = patchCoord(x, P), pz = patchCoord(z, P);
    const jxx = s * periodicBilinear(g.jxx, g.M, P, px, pz), jzz = s * periodicBilinear(g.jzz, g.M, P, px, pz), jxz = s * periodicBilinear(g.jxz, g.M, P, px, pz);
    return (1 + jxx) * (1 + jzz) - jxz * jxz;
  }

  /** The crest scale: 3σ of the drawn band's height (a Gerstner set's amplitude sum is its own worst crest). */
  maxAmplitude(): number { return 3 * Math.sqrt(Math.max(0, this.bandStats.varVertex)); }
  totalSlopeVariance(): number { return this.bandStats.slopeVarLow + this.bandStats.slopeVarShort; }
  /** Time-averaged height variance of the drawn band / the whole band (amplitude scale 1). */
  get bandVariance(): { vertex: number; normal: number } { return { vertex: this.bandStats.varVertex, normal: this.bandStats.varNormal }; }

  /**
   * The bound on |CPU drawn height − GPU drawn surface| at amplitude scale s: the pruned tail (rigorous) + the storage /
   * phase budget the WebGPU bed measures (half-float displacement ~0.5 mm at 1 m, float32 phases ~1e-3 rad): 3 mm.
   */
  heightErrorBoundM(ampScale = 1): number {
    return this.tail * ampScale + 0.003;
  }

  /** The blended h0 the GPU draws right now (read-only: probes compare the CPU mirror against a GPU readback with it). */
  get h0Current(): Float32Array { return this.h0; }

  /** Pruning facts for the lever. */
  get queryModes(): { kept: number; considered: number; tailM: number } {
    return { kept: this.order.length, considered: this.grid.vertexHalf.length, tailM: this.tail };
  }

  // ── TSL ──────────────────────────────────────────────────────────────────────────────────────────────────────────

  tsl(_uTime: unknown, _uFrameDt: unknown, mod: TslWaveModulation | null): TslWaveField {
    const patch = this.patch;
    if (!patch) throw new Error('FftWaveProvider.tsl: no GPU patch (constructed without a WebGPU renderer)');
    const P = this.grid.P, N = this.grid.N;
    const shift = this.uPatchShift;
    const amp = (mod ? mod.ampScale : this.uAmp) as unknown as FloatNode;
    const ampPrev = this.uAmpPrev as unknown as FloatNode;
    const lift = this.uLift as unknown as FloatNode, liftPrev = this.uLiftPrev as unknown as FloatNode;
    const varLow = this.uVarLow as unknown as FloatNode, varShort = this.uVarShort as unknown as FloatNode;
    // Texel X holds the field at x = X·(P/N); a texel CENTRE is at uv (X + ½)/N — hence the half-texel offset.
    const uvOf = (px: FloatNode, pz: FloatNode) => vec2(px.add(shift.x), pz.add(shift.y)).div(P).add(0.5 / N);
    const minW = this.grid.minWavelength, vertW = this.grid.vertexWavelength;

    const displacementWith = (tex: THREE.Texture, a: FloatNode, l: FloatNode) => (px: FloatNode, pz: FloatNode) => {
      const d = texture(tex, uvOf(px, pz));
      return { dx: d.x.mul(a) as FloatNode, dy: d.y.mul(a).add(l) as FloatNode, dz: d.z.mul(a) as FloatNode };
    };
    /** The exact normal of the choppy map from slopes (hx, hz) and the map's derivatives, all already × amp. */
    const choppyNormal = (hx: FloatNode, hz: FloatNode, jxx: FloatNode, jzz: FloatNode, jxz: FloatNode) => {
      const J = float(1).add(jxx).mul(float(1).add(jzz)).sub(jxz.mul(jxz)).max(0.05);
      return normalize(vec3(
        hx.negate().mul(float(1).add(jzz)).add(jxz.mul(hz)),
        J,
        hz.negate().mul(float(1).add(jxx)).add(hx.mul(jxz)),
      ));
    };
    const surface: TslGerstner = {
      displacement: displacementWith(patch.displacement, amp, lift),
      normal: (px, pz) => {
        const uv = uvOf(px, pz);
        const d = texture(patch.derivatives, uv), j = texture(patch.jacobian, uv);
        return choppyNormal(d.x.mul(amp) as FloatNode, d.y.mul(amp) as FloatNode, j.x.mul(amp) as FloatNode, j.y.mul(amp) as FloatNode, j.z.mul(amp) as FloatNode);
      },
      normalFiltered: (px, pz, footprint) => {
        // Bruneton 2010 in two bands: each band leaves the normal between 4 and 2 footprints per (its shortest)
        // wavelength — the Gerstner filter's own rule — and its slope variance joins α². Never both.
        const uv = uvOf(px, pz);
        const d = texture(patch.derivatives, uv), j = texture(patch.jacobian, uv);
        const wLow = smoothstep(footprint.mul(FOOTPRINT_FADE_LO), footprint.mul(FOOTPRINT_FADE_HI), float(vertW)) as unknown as FloatNode;
        const wShort = smoothstep(footprint.mul(FOOTPRINT_FADE_LO), footprint.mul(FOOTPRINT_FADE_HI), float(minW)) as unknown as FloatNode;
        const sx = d.z.mul(wLow).add(d.x.sub(d.z).mul(wShort)).mul(amp) as FloatNode;
        const sz = d.w.mul(wLow).add(d.y.sub(d.w).mul(wShort)).mul(amp) as FloatNode;
        const cj = wShort.mul(amp) as FloatNode; // the chop is a short-wave effect: it leaves with the short band
        const normal = choppyNormal(sx, sz, j.x.mul(cj) as FloatNode, j.y.mul(cj) as FloatNode, j.z.mul(cj) as FloatNode);
        const variance = float(1).sub(wLow).mul(varLow).add(float(1).sub(wShort).mul(varShort)).mul(amp).mul(amp) as FloatNode;
        return { normal, variance };
      },
      jacobian: (px, pz) => {
        const j = texture(patch.jacobian, uvOf(px, pz));
        const jxx = j.x.mul(amp), jzz = j.y.mul(amp), jxz = j.z.mul(amp);
        return float(1).add(jxx).mul(float(1).add(jzz)).sub(jxz.mul(jxz)) as FloatNode;
      },
    };
    const vertex: TslGerstner = { ...surface, displacement: displacementWith(patch.displacement, amp, lift) };
    const vertexPrevious: TslGerstner = { ...surface, displacement: displacementWith(patch.displacementPrev, ampPrev, liftPrev) };
    return { surface, vertex, vertexPrevious };
  }

  dispose(): void {
    this.patch?.dispose();
    this.vertexCache.clear();
  }
}
